import { useCallback, useMemo } from 'react';
import Services from '../../../../../bridge/utils/Services';
import { LogRecordType } from '../../../../../bridge/utils/LogRecord';
import Dropdown from '../../../../../components/Form/Dropdown';
import InputField from '../../../../../components/Form/InputField';
import Checkbox from '../../../../../components/Checkbox';

interface Props {
  services?: Services[];
  activeService?: Services;
  handleServiceChange?: (sservice: Services) => void;
  bots?: string[];
  activeBot?: string | undefined;
  handleBotChange?: (botId: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  maxLogs: number;
  setMaxLogs: (maxLogs: number) => void;
  amountOfLogs: number[];
  startTimestamp: string | undefined;
  setStartTimestamp: (startTimestamp: string | undefined) => void;
  endTimestamp: string | undefined | undefined;
  setEndTimestamp: (endTimestamp: string | undefined) => void;
  messageTypes?: LogRecordType[];
  setMessageTypes?: React.Dispatch<React.SetStateAction<LogRecordType[]>>;
}

const LogsFilter = (props: Props) => {
  const {
    services,
    activeService,
    handleServiceChange,
    bots,
    activeBot,
    handleBotChange,
    searchQuery,
    setSearchQuery,
    maxLogs,
    setMaxLogs,
    amountOfLogs,
    startTimestamp,
    setStartTimestamp,
    endTimestamp,
    setEndTimestamp,
    messageTypes,
    setMessageTypes,
  } = props;

  const searchField = useMemo(() => {
    return {
      label: 'Search',
      value: searchQuery,
      handleValueChange: setSearchQuery,
      shouldFit: true,
    };
  }, [searchQuery, setSearchQuery]);

  const startTimestampField = useMemo(() => {
    return {
      label: 'Start Timestamp',
      value: startTimestamp,
      handleValueChange: setStartTimestamp,
      shouldFit: true,
    };
  }, [setStartTimestamp, startTimestamp]);

  const endTimestampField = useMemo(() => {
    return {
      label: 'End Timestamp',
      value: endTimestamp,
      handleValueChange: setEndTimestamp,
      shouldFit: true,
    };
  }, [setEndTimestamp, endTimestamp]);

  const handleMessageTypeChange = useCallback(
    (messageType: LogRecordType) => {
      if (!setMessageTypes || !messageTypes) {
        return;
      }

      setMessageTypes((prev) => {
        const updated = [...prev];
        const currentValueIndex = messageTypes.findIndex(
          (t) => t === messageType,
        );

        if (currentValueIndex === -1) {
          updated.push(messageType);
          return updated;
        }

        if (prev.length === 1) {
          return prev;
        }

        updated.splice(currentValueIndex, 1);
        return updated;
      });
    },
    [messageTypes, setMessageTypes],
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderBottom: 1,
        borderBottomColor: '#242424',
        borderBottomStyle: 'solid',
        paddingBottom: 15,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {activeService && !!handleServiceChange && services && (
          <Dropdown
            config={{
              label: 'Services',
              value: activeService,
              handleValueChange: handleServiceChange,
              options: services,
            }}
          />
        )}
        {handleBotChange && (
          <Dropdown
            config={{
              label: 'Bots',
              value: activeBot,
              handleValueChange: handleBotChange,
              options: bots || [],
            }}
          />
        )}
        <InputField config={searchField} />
        <Dropdown
          config={{
            label: 'Max Logs',
            value: maxLogs,
            handleValueChange: setMaxLogs,
            options: amountOfLogs,
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'end',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: 20,
            gap: 8,
          }}
        >
          <InputField config={startTimestampField} />
          <InputField config={endTimestampField} />
        </div>

        {!!messageTypes && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Checkbox
              label={'Info messages'}
              value={!!messageTypes.find((t) => t === LogRecordType.Info)}
              handleValueChange={() =>
                handleMessageTypeChange(LogRecordType.Info)
              }
              color="green"
            />
            <Checkbox
              label={'Variable messages'}
              value={!!messageTypes.find((t) => t === LogRecordType.Variable)}
              handleValueChange={() =>
                handleMessageTypeChange(LogRecordType.Variable)
              }
              color="white"
            />
            <Checkbox
              label={'Error messages'}
              value={!!messageTypes.find((t) => t === LogRecordType.Error)}
              handleValueChange={() =>
                handleMessageTypeChange(LogRecordType.Error)
              }
              color="red"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LogsFilter;
